'use client';

import { NumberFormats, ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { Link } from '../../../Link/Link';
import { ScratchcardPlayButton } from '../../../scratchcards/ScratchcardGameLauncher/ScratchcardPlayButton';
import type { CheckoutPurchaseConfirmation, LotteryProduct, ScratchcardProduct } from '../../types';

type OrderDetailsProps = {
  orderDetails: CheckoutPurchaseConfirmation['orderDetails'];
  products: CheckoutPurchaseConfirmation['products'];
  pricing: CheckoutPurchaseConfirmation['pricing'];
};

function SummaryDivider() {
  return (
    <div aria-hidden="true">
      <div className="border-t border-border" />
    </div>
  );
}

function ProductDescription({ product }: { product: LotteryProduct | ScratchcardProduct }) {
  const t = useTranslations('thank_you.product');

  if (product.type === 'scratchcard') {
    return <p className="paragraph-s text-muted-foreground">{t('scratchcards', { count: product.quantity })}</p>;
  }

  return <p className="paragraph-s text-muted-foreground">{t('lottery_lines', { lines: product.lines, draws: product.draws })}</p>;
}

function ProductName({ product }: { product: LotteryProduct | ScratchcardProduct }) {
  const tLotteries = useTranslations('lotteries');

  if (product.type === 'scratchcard') {
    return (
      <p className="paragraph-strong" data-testid={`thank-you-product-name-${product.type}`}>
        {product.name}
      </p>
    );
  }

  const key = `${product.lotteryId}.name`;
  const lotteryName = tLotteries.has(key) ? tLotteries(key) : product.lotteryId;

  return (
    <p className="paragraph-strong" data-testid={`thank-you-product-name-${product.type}`}>
      {lotteryName}
    </p>
  );
}

function ProductAction({ product }: { product: LotteryProduct | ScratchcardProduct }) {
  const t = useTranslations('thank_you.actions');

  if (product.type === 'scratchcard') {
    if (!product.title) {
      return null;
    }

    return (
      <ScratchcardPlayButton scratchcardName={product.title} size="s" data-testid="thank-you-play-action" data-track-name="Play">
        {t('play')}
      </ScratchcardPlayButton>
    );
  }

  return (
    <Link href={`${ROUTES.LOTTO.PURCHASES}/${product.ticketId}`} size="m" className="shrink-0" data-testid="thank-you-view-numbers-action" data-track-name="Go To Purchase">
      {t('view_numbers')}
    </Link>
  );
}

function ProductRow({ product }: { product: LotteryProduct | ScratchcardProduct }) {
  return (
    <div className="flex items-center justify-between gap-4" data-testid={`thank-you-product-row-${product.type}`}>
      <div className="flex flex-col gap-0.5">
        <ProductName product={product} />
        <ProductDescription product={product} />
      </div>
      <ProductAction product={product} />
    </div>
  );
}

function OrderDetails({ orderDetails, products, pricing }: OrderDetailsProps) {
  const t = useTranslations('thank_you');
  const formatCurrency = useCurrencyFormatter();
  const formatAmount = (amount: number) => formatCurrency(amount).format(NumberFormats.WITH_DECIMALS).get();
  const { lottery, scratchcard } = products;

  return (
    <section
      className="w-full rounded-lg border border-border dark:border-container bg-container overflow-hidden flex flex-col gap-3 p-5"
      data-testid="thank-you-order-summary"
      data-track-container="Payment Details"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="paragraph-s" data-testid="thank-you-order-heading">
            {t('order_details.heading')}
          </span>
          <span className="paragraph-s text-muted-foreground" data-testid="thank-you-order-number">
            {t('order_details.order_number', { orderId: orderDetails.orderId })}
          </span>
        </div>

        <SummaryDivider />

        <div className="flex flex-col gap-4">
          {lottery ? (
            <div className={scratchcard ? 'border-b border-border pb-4' : undefined}>
              <ProductRow product={lottery} />
            </div>
          ) : null}
          {scratchcard ? <ProductRow product={scratchcard} /> : null}
        </div>
      </div>

      <SummaryDivider />

      <div className="flex flex-col gap-2" data-track-value={formatAmount(pricing.total)}>
        <div className="flex items-center justify-between gap-4" data-testid="thank-you-subtotal-row">
          <span className="paragraph-s">{t('order_details.subtotal', { count: pricing.itemCount })}</span>
          <span className="paragraph-s" data-testid="thank-you-subtotal">
            {formatAmount(pricing.subtotal)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4" data-testid="thank-you-bonus-row">
          <span className="paragraph-s">{t('order_details.bonus')}</span>
          <span className="paragraph-s" data-testid="thank-you-bonus">
            {pricing.bonusDiscount > 0 ? `-${formatAmount(pricing.bonusDiscount)}` : formatAmount(pricing.bonusDiscount)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-border pt-2" data-testid="thank-you-total-row">
          <span className="paragraph-strong">{t('order_details.total')}</span>
          <span className="paragraph-strong" data-testid="thank-you-total">
            {formatAmount(pricing.total)}
          </span>
        </div>
      </div>
    </section>
  );
}

export { OrderDetails, type OrderDetailsProps };
